import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// Import Shared Components
import { WithAutoLogout } from 'shared-components';
import {
  DIRECT_SEND,
  CARD_ISSUING,
  COLLECTIONS,
  API_KEY_ENABLED,
  INCIDENT_PORTAL_DETAILS,
  INCIDENT_PORTAL,
} from 'shared-components/src/utils/services';
// Import Shared Utils
import auth from 'shared-components/src/utils/auth';
// Import Layouts
import PrivateLayout from '../layouts/Private';
import PrivateRoute from '../layouts/components/PrivateRoute';
// Import Dashboard Route Components
import Dashboard from './Dashboard';
// Import Direct Send Route Components
import AccountHolders from './DirectSend/AccountHolders';
import AccountHolderDetails from './DirectSend/AccountHolderDetails';
import UploadAccountHolders from './DirectSend/AccountHolders/components/UploadAccountHolders';
import CreateTransfer from './DirectSend/CreateTransfer';
import Limits from './DirectSend/Limits';
import Reports from './DirectSend/Reports';
import TransferDetails from './DirectSend/TransferDetails';
import Transfers from './DirectSend/Transfers';
import CreateDirectSendReportSubscription from './DirectSend/CreateReportSubscription';
// Import Card Issuing Route Components
import Programs from './CardIssuing/Programs';
import ProgramDetails from './CardIssuing/ProgramDetails';
import CreateReportSubscription from './CardIssuing/CreateReportSubscription';
import Cardholders from './CardIssuing/Cardholders';
import CardholderDetails from './CardIssuing/CardholderDetails';
import KycDetails from './CardIssuing/KycDetails';
import CreateCardholders from './CardIssuing/CreateCardholders';
import ValueLoads from './CardIssuing/ValueLoads';
import CreateValueLoads from './CardIssuing/CreateValueLoads';
import CreateValueUnloads from './CardIssuing/CreateValueUnloads';
import UpdateCard from './CardIssuing/UpdateCard';
import ActivateLoadCard from './CardIssuing/ActivateLoadCard';
import UnloadByPRN from './CardIssuing/UnloadByPRN';
// Import Collections Route Components
import CreateCollection from './Collections/CreateCollection';
import CollectionDetails from './Collections/CollectionDetails';
import Collections from './Collections/Collections';
import CollectionConfiguration from './Collections/CollectionConfiguration';
import CollectionReports from './Collections/CollectionReports';
// Import Settings Route Components
import Company from './Settings/Company';
import TeamInfo from './Settings/TeamInfo';
import TimeZone from './Settings/TimeZone';
// Import Api Route Components
import Api from './Api';
// Import Help Route Components
import Help from './Help';
// Import Page Not Found Route Components
import NotFound from './NotFound';
// Import Common Utils
import { refresh } from '../utils/auth';
import { admin, user } from '../utils/roles';
import SystemStatus from './SystemStatus';
import SystemIncidents from './SystemStatus/SystemIncidents';

// Helper component to set page title
const PageWrapper = ({ title, setTitle, children }) => {
  useEffect(() => {
    if (title && setTitle) {
      setTitle(title);
    }
  }, [title, setTitle]);

  return children;
};

function Private(props) {
  if (!auth.isAuthenticated()) return <Navigate to="/auth/login" replace />;
  const [pageTitle, setPageTitle] = useState('Dashboard');

  return (
    <PrivateLayout
      {...props}
      pageTitle={pageTitle}
      id="private"
    >
      <Routes>
        <Route
          path="/"
          element={
            <PrivateRoute
              allowedRoles={user}
              element={
                <PageWrapper title="Dashboard" setTitle={setPageTitle}>
                  <Dashboard />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/create"
          element={
            <PrivateRoute
              title="Create Transfer"
              allowedRoles={user}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Create Transfer" setTitle={setPageTitle}>
                  <CreateTransfer />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/transfers"
          element={
            <PrivateRoute
              title="Transfer History"
              allowedRoles={user}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Transfer History" setTitle={setPageTitle}>
                  <Transfers />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/transfers/:id"
          element={
            <PrivateRoute
              title="Transfer Details"
              breadcrumbs={[
                { name: 'Transfers', location: '/direct-send/transfers' },
              ]}
              allowedRoles={user}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Transfer Details" setTitle={setPageTitle}>
                  <TransferDetails />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/account-holders"
          element={
            <PrivateRoute
              title="Account Holders"
              allowedRoles={admin}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Account Holders" setTitle={setPageTitle}>
                  <AccountHolders />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/account-holders/upload"
          element={
            <PrivateRoute
              title="Upload Account Holders"
              breadcrumbs={[
                {
                  name: 'Account Holders',
                  location: '/direct-send/account-holders',
                },
              ]}
              allowedRoles={admin}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Upload Account Holders" setTitle={setPageTitle}>
                  <UploadAccountHolders />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/account-holders/:id"
          element={
            <PrivateRoute
              title="Account Holder Details"
              breadcrumbs={[
                {
                  name: 'Account Holders',
                  location: '/direct-send/account-holders',
                },
              ]}
              allowedRoles={admin}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Account Holder Details" setTitle={setPageTitle}>
                  <AccountHolderDetails />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/reports"
          element={
            <PrivateRoute
              title="Reports"
              allowedRoles={user}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Reports" setTitle={setPageTitle}>
                  <Reports />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/reports/create"
          element={
            <PrivateRoute
              title="Create Report Subscription"
              allowedRoles={user}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Create Report Subscription" setTitle={setPageTitle}>
                  <CreateDirectSendReportSubscription />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/direct-send/limits"
          element={
            <PrivateRoute
              title="Limits"
              allowedRoles={admin}
              service={DIRECT_SEND}
              element={
                <PageWrapper title="Limits" setTitle={setPageTitle}>
                  <Limits />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/programs"
          element={
            <PrivateRoute
              title="Programs"
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Programs" setTitle={setPageTitle}>
                  <Programs />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/programs/:id"
          element={
            <PrivateRoute
              title="Program Details"
              breadcrumbs={[
                {
                  name: 'Programs',
                  location: '/card-issuing/programs',
                },
              ]}
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Program Details" setTitle={setPageTitle}>
                  <ProgramDetails />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/programs/:id/reports"
          element={
            <PrivateRoute
              title="Create Report Subscription"
              breadcrumbs={[
                {
                  name: 'Programs',
                  location: '/card-issuing/programs',
                },
                // TODO: Need to find solution to pass ID to breadcrumbs
                // {
                //   name: 'Program Details',
                //   location: '/card-issuing/programs/:id',
                // },
              ]}
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Create Report Subscription" setTitle={setPageTitle}>
                  <CreateReportSubscription />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/cardholders"
          element={
            <PrivateRoute
              title="Cardholders"
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Cardholders" setTitle={setPageTitle}>
                  <Cardholders />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/cardholders/create"
          element={
            <PrivateRoute
              title="Create Cardholders"
              breadcrumbs={[
                { name: 'Cardholders', location: '/card-issuing/cardholders' },
              ]}
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Create Cardholders" setTitle={setPageTitle}>
                  <CreateCardholders />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/kyc_results/:id"
          element={
            <PrivateRoute
              title="KYC Details"
              breadcrumbs={[
                { name: 'Cardholders', location: '/card-issuing/cardholders' },
              ]}
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="KYC Details" setTitle={setPageTitle}>
                  <KycDetails />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/cardholders/:id"
          element={
            <PrivateRoute
              title="Cardholder Details"
              breadcrumbs={[
                { name: 'Cardholders', location: '/card-issuing/cardholders' },
              ]}
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Cardholder Details" setTitle={setPageTitle}>
                  <CardholderDetails />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/value-loads"
          element={
            <PrivateRoute
              title="Value Loads"
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Value Loads" setTitle={setPageTitle}>
                  <ValueLoads />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/value-loads/load"
          element={
            <PrivateRoute
              title="Create Value Loads"
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Create Value Loads" setTitle={setPageTitle}>
                  <CreateValueLoads />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/value-loads/unload"
          element={
            <PrivateRoute
              title="Create Value Unloads"
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Create Value Unloads" setTitle={setPageTitle}>
                  <CreateValueUnloads />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/activate-load-card"
          element={
            <PrivateRoute
              title="Activate & Load Card"
              allowedRoles={admin}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Activate & Load Card" setTitle={setPageTitle}>
                  <ActivateLoadCard />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/unload-card"
          element={
            <PrivateRoute
              title="Unload Card by Proxy"
              allowedRoles={admin}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Unload Card by Proxy" setTitle={setPageTitle}>
                  <UnloadByPRN />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/card-issuing/update-card"
          element={
            <PrivateRoute
              title="Update card"
              allowedRoles={user}
              service={CARD_ISSUING}
              element={
                <PageWrapper title="Update card" setTitle={setPageTitle}>
                  <UpdateCard />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/collections/create"
          element={
            <PrivateRoute
              title="Create Collection"
              allowedRoles={user}
              service={COLLECTIONS}
              element={
                <PageWrapper title="Create Collection" setTitle={setPageTitle}>
                  <CreateCollection />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/collections/collections"
          element={
            <PrivateRoute
              title="Collection History"
              allowedRoles={user}
              service={COLLECTIONS}
              element={
                <PageWrapper title="Collection History" setTitle={setPageTitle}>
                  <Collections />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/collections/collections/:id"
          element={
            <PrivateRoute
              title="Collection Details"
              breadcrumbs={[
                { name: 'Collections', location: '/collections/collections' },
              ]}
              allowedRoles={user}
              service={COLLECTIONS}
              element={
                <PageWrapper title="Collection Details" setTitle={setPageTitle}>
                  <CollectionDetails />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/collections/configuration"
          element={
            <PrivateRoute
              title="Collections Configuration"
              allowedRoles={user}
              service={COLLECTIONS}
              element={
                <PageWrapper title="Collections Configuration" setTitle={setPageTitle}>
                  <CollectionConfiguration />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/collections/reports"
          element={
            <PrivateRoute
              title="Collection Reports"
              allowedRoles={user}
              service={COLLECTIONS}
              element={
                <PageWrapper title="Collections Reports" setTitle={setPageTitle}>
                  <CollectionReports />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/api-keys"
          element={
            <PrivateRoute
              title="API Keys"
              allowedRoles={user}
              service={API_KEY_ENABLED}
              element={
                <PageWrapper title="API Keys" setTitle={setPageTitle}>
                  <Api />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/settings/company"
          element={
            <PrivateRoute
              title="Company Settings"
              allowedRoles={user}
              element={
                <PageWrapper title="Company Settings" setTitle={setPageTitle}>
                  <Company />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/settings/team"
          element={
            <PrivateRoute
              title="Team Members"
              allowedRoles={admin}
              element={
                <PageWrapper title="Team Members" setTitle={setPageTitle}>
                  <TeamInfo />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/settings/timezone"
          element={
            <PrivateRoute
              title="Time Zone Settings"
              allowedRoles={user}
              element={
                <PageWrapper title="Time Zone Settings" setTitle={setPageTitle}>
                  <TimeZone />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/system-status"
          element={
            <PrivateRoute
              title="System Status"
              allowedRoles={user}
              service={INCIDENT_PORTAL}
              element={
                <PageWrapper title="System Status" setTitle={setPageTitle}>
                  <SystemStatus />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/system-incidents"
          element={
            <PrivateRoute
              title="System Status"
              allowedRoles={user}
              service={INCIDENT_PORTAL_DETAILS}
              element={
                <PageWrapper title="System Status" setTitle={setPageTitle}>
                  <SystemIncidents />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path="/help"
          element={
            <PrivateRoute
              title="Contact Us"
              allowedRoles={user}
              element={
                <PageWrapper title="Contact Us" setTitle={setPageTitle}>
                  <Help />
                </PageWrapper>
              }
            />
          }
        />
        <Route
          path=""
          element={
            <PrivateRoute
              title="Page Not Found"
              allowedRoles={user}
              element={
                <PageWrapper title="Page Not Found" setTitle={setPageTitle}>
                  <NotFound />
                </PageWrapper>
              }
            />
          }
        />
      </Routes>
    </PrivateLayout>
  );
}

export default WithAutoLogout(Private, refresh);
